import { useState, useCallback } from 'react';
import {
  TABLE_COLUMN_TYPES,
  generateId,
} from '@/features/quote-and-apply-builder';

interface UseTableColumnsEditorParams {
  columns: PolicyFormTableColumn[];
  onChange: (columns: PolicyFormTableColumn[]) => void;
}

export function useTableColumnsEditor({ columns, onChange }: UseTableColumnsEditorParams) {
  const [editingColumn, setEditingColumn] = useState<PolicyFormTableColumn | null>(null);

  const handleAddColumn = useCallback(() => {
    const newColumn: PolicyFormTableColumn = {
      id: generateId('col'),
      title: 'New Column',
      type: 'text',
    };
    onChange([...columns, newColumn]);
  }, [columns, onChange]);

  const handleUpdateColumn = useCallback(
    (updatedColumn: PolicyFormTableColumn) => {
      onChange(columns.map(col => (col.id === editingColumn?.id ? updatedColumn : col)));
      setEditingColumn(null);
    },
    [columns, editingColumn, onChange],
  );

  const handleRemoveColumn = useCallback(
    (columnId: string) => {
      onChange(columns.filter(col => col.id !== columnId));
    },
    [columns, onChange],
  );

  const handleMoveColumn = useCallback(
    (index: number, direction: 'up' | 'down') => {
      if (
        (direction === 'up' && index === 0) ||
        (direction === 'down' && index === columns.length - 1)
      ) {
        return;
      }
      const updated = [...columns];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];
      onChange(updated);
    },
    [columns, onChange],
  );

  const getTypeLabel = useCallback((type: PolicyFormTableColumn['type']) => {
    return TABLE_COLUMN_TYPES.find(columnType => columnType.value === type)?.label || type;
  }, []);

  const handleOpenColumnSettings = useCallback((column: PolicyFormTableColumn) => {
    setEditingColumn(column);
  }, []);

  const handleCloseColumnSettings = useCallback(() => {
    setEditingColumn(null);
  }, []);

  return {
    editingColumn,
    handleAddColumn,
    handleUpdateColumn,
    handleRemoveColumn,
    handleMoveColumn,
    getTypeLabel,
    handleOpenColumnSettings,
    handleCloseColumnSettings,
  };
}
